import { FormEvent, useState } from 'react';
import chevDownIcon from 'src/assets/images/icons/chevron-down.svg';
import ContinueMonitoring from '../ContinueMonitoring';
import { MONITORING_TYPES } from 'src/constants';
import * as notify from 'src/services/notification.service';

interface Props {
  screen: (searchParams: {
    formData: FormData;
    fileUploadProgressHandler: (progress: number) => void;
  }) => void;
}

const BatchSearchForm: (props: Props) => JSX.Element = (props: Props) => {
  const defaultSearchParams = {
    fileName: '',
    file: null,
    customerType: 'Individual',
    customFilename: '',
  };
  const [searchParameters, setSearchParameters] = useState<{
    customerType: string;
    file: File | null;
    fileName: string;
    customFilename: string;
  }>({ ...defaultSearchParams });
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [isContinueMonitoring, setContinueMonitoring] = useState(true);
  const [monitoringType, setMonitoringType] = useState(MONITORING_TYPES[0]);

  /**
   * This function performs the basic validation before screening
   * @param e
   */
  const screen = (e: FormEvent) => {
    e.preventDefault();
    let valid = true;
    /**
     * First validate the input
     */
    if (
      !searchParameters.file ||
      !searchParameters.customerType ||
      !searchParameters.customFilename ||
      searchParameters.customFilename.length > 24
    ) {
      valid = false;
    }
    /*******************************/
    if (valid) {
      let formData = new FormData();

      formData.append('file', searchParameters.file!);
      formData.append('customerType', searchParameters.customerType);
      formData.append('monitoring', isContinueMonitoring ? monitoringType : 'Never');
      formData.append('customFilename', searchParameters.customFilename);
      props.screen({
        formData,
        fileUploadProgressHandler: (progress: number) => {
          setFileUploadProgress(progress);
        },
      });
    }
  };

  const clear = () => {
    setSearchParameters({ ...defaultSearchParams });
    setMonitoringType(MONITORING_TYPES[0]);
    setContinueMonitoring(true);
  };

  return (
    <div className="card overflow-visible" data-testid="batch-upload-form">
      <form onSubmit={screen}>
        <div className="d-flex gap-3 align-items-end flex-wrap">
          <div className="flex-grow-1">
            <label htmlFor="custom_filename" className="form-label">
              Name (max 24 characters)*
            </label>
            <input
              name="custom_filename"
              type="text"
              value={searchParameters.customFilename}
              id="custom_filename"
              className="form-control"
              onChange={(e) => {
                let validAlphaNumericPattern = /^([a-zA-Z0-9 _-]*)$/;
                if (e.target.value.length < 25 && validAlphaNumericPattern.test(e.target.value)) {
                  setSearchParameters({
                    ...searchParameters,
                    customFilename: e.target.value,
                  });
                }
              }}
            />
          </div>

          <div className="flex-grow-1 ">
            <label className="form-label">Customer Type*</label>
            <select
              name="customer_type_select"
              className="form-control"
              style={{ backgroundImage: `url(${chevDownIcon})` }}
              value={searchParameters.customerType}
              onChange={(e) => {
                setSearchParameters({ ...searchParameters, customerType: e.target.value });
              }}
            >
              <option disabled>Select customer type</option>
              <option value="Business">Businesses</option>
              <option value="Individual">Individuals</option>
            </select>
          </div>
          <div className="flex-grow-1 position-relative upload-file">
            <label htmlFor="fileForUpload" className="form-label">
              CSV or XLSX File (max. 50 MB)*
            </label>
            <input
              name="file_input_disabled"
              type="text"
              value={searchParameters.fileName}
              id="fileForUpload"
              placeholder="Select file"
              className="form-control"
              disabled={true}
            />
            <input
              name="file_input"
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => {
                if (e.target && e.target.files && e.target.files.length) {
                  const file = e.target.files[0];
                  if (!/\.(csv|xlsx)$/i.test(file.name) || file.size > 50 * 1024 * 1024) {
                    notify.error('Select a CSV or XLSX file no larger than 50 MB');
                    e.target.value = '';
                    setSearchParameters({ ...searchParameters, file: null, fileName: '' });
                    return;
                  }
                  setSearchParameters({
                    ...searchParameters,
                    file,
                    fileName: file.name,
                  });
                }
              }}
              className="form-control"
            />
            <button name="file_upload_btn" type="button" className="button btn-with-icon grey-btn">
              <div>
                <img src={require('src/assets/images/icons/browse-icon.svg').default} alt="" />
                Browse
              </div>
            </button>
            {/* 'visible' and 'invisible' for toggle progressbar */}
            <div
              className={`progress ${
                !fileUploadProgress || fileUploadProgress === 100 ? 'invisible' : ''
              }`}
            >
              <div
                className="progress-bar bg-green"
                role="progressbar"
                style={{ width: `${fileUploadProgress}%` }}
              />
            </div>
          </div>
          <div className="button-group text-right">
            <button
              name="clear-form-btn"
              className="button btn-ghost"
              onClick={clear}
              type="button"
            >
              Clear
            </button>
            <button
              name="submit-form-btn"
              className="button btn-with-icon"
              onClick={screen}
              type="submit"
            >
              <img src={require('src/assets/images/icons/screen-icon.svg').default} alt="" /> Screen
            </button>
          </div>
        </div>
        <ContinueMonitoring
          isContinueMonitoring={isContinueMonitoring}
          setContinueMonitoring={setContinueMonitoring}
          monitoringType={monitoringType}
          setMonitoringType={setMonitoringType}
        />
      </form>
    </div>
  );
};

export default BatchSearchForm;
